import { useState } from 'react';
import TurnstileWidget from '../components/TurnstileWidget';
import {
  validateDeletionEmail,
  validateDeletionRequest,
} from '../lib/formValidation';
import {
  requestAccountDeletionCode,
  sendAccountDeletionRequest,
} from '../services/supportApi';

const deletionTypes = [
  ['ACCOUNT_ONLY', 'Hesabımı silmek istiyorum'],
  ['ACCOUNT_AND_DATA', 'Hesabımı ve ilişkili verilerimi silmek istiyorum'],
  ['SPECIFIC_DATA', 'Belirli verilerimi silmek istiyorum'],
  ['OTHER', 'Diğer'],
];

export default function DeleteAccountPage() {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState({
    email: '',
    code: '',
    deletionRequestType: '',
    message: '',
    company: '',
    confirmationsAccepted: false,
    subscriptionCancellationAcknowledged: false,
  });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [widgetKey, setWidgetKey] = useState(0);

  const update = (event) => {
    const { name, value, type, checked } = event.target;
    setValues((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const requestCode = async (event) => {
    event.preventDefault();
    const nextErrors = validateDeletionEmail({
      email: values.email,
      turnstileToken,
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const response = await requestAccountDeletionCode({
        email: values.email,
        company: values.company,
        turnstileToken,
      });
      setResult({ success: true, message: response.message });
      setStep(2);
      setTurnstileToken('');
      setErrors({});
    } catch (error) {
      setErrors(error.fieldErrors || {});
      setResult({ success: false, message: error.message });
      setTurnstileToken('');
      setWidgetKey((current) => current + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const createRequest = async (event) => {
    event.preventDefault();
    const nextErrors = validateDeletionRequest({
      ...values,
      turnstileToken,
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const response = await sendAccountDeletionRequest({
        ...values,
        turnstileToken,
      });
      setResult({
        success: true,
        message: response.message,
        requestId: response.requestId,
      });
      setStep(3);
    } catch (error) {
      setErrors(error.fieldErrors || {});
      setResult({ success: false, message: error.message });
      setTurnstileToken('');
      setWidgetKey((current) => current + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="page-section deletion-page">
      <div className="shell">
        <div className="legal-hero compact-hero">
          <span className="eyebrow">Hesap kontrolü</span>
          <h1>Hesabını ve verilerini silme başvurusu</h1>
          <p>
            Kayıtlı e-posta adresini doğrulayarak güvenli bir başvuru
            oluşturabilirsin. Başvurular en geç 30 gün içinde sonuçlandırılır.
          </p>
        </div>

        <ol className="process-progress" aria-label="Başvuru adımları">
          <li className={step >= 1 ? 'is-active' : ''}>
            <span>1</span> E-posta doğrulama
          </li>
          <li className={step >= 2 ? 'is-active' : ''}>
            <span>2</span> Talep ayrıntıları
          </li>
          <li className={step >= 3 ? 'is-active' : ''}>
            <span>3</span> Başvuru tamamlandı
          </li>
        </ol>

        <div className="deletion-layout">
          <div className="deletion-info">
            <h2>İşlem nasıl ilerler?</h2>
            <ol>
              <li>Kayıtlı e-posta adresine 6 haneli kod gönderilir.</li>
              <li>Kod 10 dakika geçerlidir ve en fazla 5 kez denenebilir.</li>
              <li>Doğrulanmış başvuru destek panelinde incelemeye alınır.</li>
              <li>Sonuç, başvuru sırasında kullandığın e-postaya bildirilir.</li>
            </ol>
            <div className="subscription-note">
              <strong>Aktif premium aboneliğin varsa</strong>
              <p>
                Hesabı silmek Google Play aboneliğini otomatik iptal etmez.
                Aboneliği önce Google Play üzerinden ayrıca iptal etmelisin.
              </p>
            </div>
          </div>

          <div className="form-card deletion-form-card">
            {step === 1 && (
              <form onSubmit={requestCode} noValidate>
                <span className="form-step-label">1. adım</span>
                <h2>Kayıtlı e-posta adresin</h2>
                <p className="form-description">
                  Hesap bulunup bulunmadığı güvenlik nedeniyle ekranda açıklanmaz.
                </p>
                <label className="field">
                  <span>E-posta</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={values.email}
                    onChange={update}
                    aria-invalid={Boolean(errors.email)}
                    required
                  />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </label>
                <label className="honeypot" aria-hidden="true">
                  Şirket
                  <input
                    name="company"
                    value={values.company}
                    onChange={update}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </label>
                <TurnstileWidget
                  key={'email-' + widgetKey}
                  onToken={setTurnstileToken}
                  error={errors.turnstileToken}
                />
                {result && (
                  <div className={'form-result ' + (result.success ? 'is-success' : 'is-error')}>
                    {result.message}
                  </div>
                )}
                <button className="button button-primary form-submit" disabled={submitting}>
                  {submitting ? 'Gönderiliyor…' : 'Doğrulama kodu gönder'}
                </button>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={createRequest} noValidate>
                <span className="form-step-label">2. adım</span>
                <h2>Talep ayrıntıları</h2>
                <p className="form-description">
                  Kod <strong>{values.email}</strong> adresine gönderildiyse
                  aşağıya gir.
                </p>
                <label className="field">
                  <span>6 haneli kod</span>
                  <input
                    className="code-input"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength="6"
                    value={values.code}
                    onChange={(event) => {
                      event.target.value = event.target.value.replace(/\D/g, '');
                      update(event);
                    }}
                    aria-invalid={Boolean(errors.code)}
                    required
                  />
                  {errors.code && <span className="field-error">{errors.code}</span>}
                </label>
                <label className="field">
                  <span>Talep türü</span>
                  <select
                    name="deletionRequestType"
                    value={values.deletionRequestType}
                    onChange={update}
                    aria-invalid={Boolean(errors.deletionRequestType)}
                    required
                  >
                    <option value="">Talep türü seçin</option>
                    {deletionTypes.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  {errors.deletionRequestType && (
                    <span className="field-error">{errors.deletionRequestType}</span>
                  )}
                </label>
                <label className="field">
                  <span>Açıklama <small>İsteğe bağlı</small></span>
                  <textarea
                    name="message"
                    rows="5"
                    maxLength="3000"
                    value={values.message}
                    onChange={update}
                  />
                </label>
                <label className="checkbox-field">
                  <input
                    type="checkbox"
                    name="confirmationsAccepted"
                    checked={values.confirmationsAccepted}
                    onChange={update}
                  />
                  <span>
                    Bu işlemin hesabıma erişimimi kalıcı olarak sonlandırabileceğini
                    ve silinen verilerin geri getirilemeyebileceğini anlıyorum.
                  </span>
                </label>
                {errors.confirmationsAccepted && (
                  <span className="field-error">{errors.confirmationsAccepted}</span>
                )}
                <label className="checkbox-field">
                  <input
                    type="checkbox"
                    name="subscriptionCancellationAcknowledged"
                    checked={values.subscriptionCancellationAcknowledged}
                    onChange={update}
                  />
                  <span>
                    Aktif aboneliğim varsa Google Play üzerinden ayrıca iptal
                    etmem gerektiğini anlıyorum.
                  </span>
                </label>
                {errors.subscriptionCancellationAcknowledged && (
                  <span className="field-error">
                    {errors.subscriptionCancellationAcknowledged}
                  </span>
                )}
                <TurnstileWidget
                  key={'request-' + widgetKey}
                  onToken={setTurnstileToken}
                  error={errors.turnstileToken}
                />
                {result && (
                  <div className={'form-result ' + (result.success ? 'is-success' : 'is-error')}>
                    {result.message}
                  </div>
                )}
                <div className="form-action-row">
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setResult(null);
                      setErrors({});
                      setTurnstileToken('');
                    }}
                  >
                    Geri dön
                  </button>
                  <button className="button button-primary" disabled={submitting}>
                    {submitting ? 'Başvuru oluşturuluyor…' : 'Başvuruyu oluştur'}
                  </button>
                </div>
              </form>
            )}

            {step === 3 && result && (
              <div className="deletion-success" role="status">
                <div className="success-mark" aria-hidden="true">✓</div>
                <span className="form-step-label">Başvuru alındı</span>
                <h2>Talebin doğrulandı</h2>
                <p>{result.message}</p>
                <div className="request-id-card">
                  <small>Başvuru numarası</small>
                  <strong>{result.requestId}</strong>
                </div>
                <p className="form-description">
                  Bu numarayı sakla. Sonuçlandırma hedefimiz en geç 30 gündür.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
